import React, { useEffect, useMemo, useRef, useState } from 'react';
import IllustratedCharacter from '../../../components/character/IllustratedCharacter';
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../utils/firebase';
import { getCharacterPreset, getEquippedCharacterImageUrl, type CharacterPresetId, DEFAULT_CHARACTER_PRESET } from '../../../utils/characterPresets';
import { CHARACTER_LAYER_RENDER_ORDER, type CharacterLoadout } from '../../../types/character';
import { FURNITURE_PRESETS, FURNITURE_THEME_LABELS, ROOM_SURFACE_PRESETS, type FurnitureKey, type FurniturePreset, type FurnitureSlot, type FurnitureTheme, type RoomSurfaceKey, type RoomSurfaceSlot } from '../../../types/myHome';
import MyHomePet from './VirtualPet/MyHomePet';
import baseStyles from './StudentProfile.module.css';
import enhancedStyles from './StudentProfileEnhancements.module.css';

const styles = { ...baseStyles, ...enhancedStyles };

type DecorKey = FurnitureKey;
type RoomSlot = FurnitureSlot;
type FurnitureRotation = 0 | 1;
type FurniturePlacement = { x: number; y: number; rotation: FurnitureRotation };
type FurniturePlacements = Partial<Record<DecorKey, FurniturePlacement>>;
type WallPosition = { x: number; y: number };

const GRID_COLUMNS = 11;
// 양쪽 벽의 가구 배치선이 몰딩을 넘지 않도록 북쪽 경계를 같은 높이로 둔다.
const GRID_MIN_X = 0;
const GRID_MIN_Y = -1;
const GRID_ROWS = 10;
// 3:2 room canvas에서 실제 화면 픽셀 기준 2:1 아이소메트릭 축을 유지한다.
// 배경 원본(1536×1024)의 바닥 타일 간격을 사용하고 위쪽으로 y=-1 행을 연다.
const ISO_CENTER_X = 50;
// 일러스트 바닥의 실제 첫 격자선에 맞춰 배치 좌표계 전체를 한 칸 아래로 내린다.
const ISO_TOP_Y = 28.47;
const ISO_TILE_X = 4.12;
const ISO_TILE_Y = 3.27;
const floorSlotDefaults: Record<Exclude<RoomSlot, 'wall'>, { x: number; y: number }> = {
  desk: { x: 6, y: 1 }, floor: { x: 3, y: 3 }, corner: { x: 6, y: 3 }, bed: { x: 1, y: 2 },
};
const getFootprint = (item: Pick<FurniturePreset, 'slot' | 'gridSize'>, rotation: FurnitureRotation) => {
  const base = item.gridSize || (item.slot === 'bed' ? { width: 3, depth: 2 } : item.slot === 'desk' ? { width: 2, depth: 2 } : item.slot === 'floor' ? { width: 2, depth: 2 } : { width: 1, depth: 1 });
  return rotation === 1 ? { width: base.depth, depth: base.width } : base;
};
const fitPlacementsToGrid = (value: FurniturePlacements): FurniturePlacements => Object.entries(value).reduce<FurniturePlacements>((result, [key, placement]) => {
  if (!placement) return result;
  const item = FURNITURE_PRESETS.find((entry) => entry.key === key);
  if (!item || item.slot === 'wall') return result;
  const rotation: FurnitureRotation = placement.rotation === 1 ? 1 : 0;
  const footprint = getFootprint(item, rotation);
  result[key as DecorKey] = {
    x: Math.max(GRID_MIN_X, Math.min(Math.round(placement.x), GRID_COLUMNS - footprint.width)),
    y: Math.max(GRID_MIN_Y, Math.min(Math.round(placement.y), GRID_ROWS - footprint.depth)),
    rotation,
  };
  return result;
}, {});
const gridToPercent = (x: number, y: number) => ({ left: ISO_CENTER_X + (x - y) * ISO_TILE_X, top: ISO_TOP_Y + (x + y) * ISO_TILE_Y });
const gridCellToPercent = (x: number, y: number) => gridToPercent(x + 0.5, y + 0.5);
// 벽 장식 선택 영역은 방 너비의 12%인 정사각형이다. 3:2 방 캔버스에서는
// 세로 반지름이 약 9%가 되므로, 경사진 몰딩에서 가로 반지름이 만드는 높이
// 변화까지 더해 장식의 네 모서리가 모두 벽 안에 머물게 한다.
const getWallBounds = (x: number) => {
  const distanceFromCorner = Math.abs(x - 50);
  // 1536×1024 배경 에셋의 민트 벽면 픽셀을 기준으로 한 실제 경계다.
  // 위쪽은 중앙 3.1%, 아래쪽은 중앙 25.1%에서 시작해 거의 평행하게 내려간다.
  return { top: 3.1 + distanceFromCorner * 0.73, bottom: 25.1 + distanceFromCorner * 0.75 };
};
const getWallYRange = (x: number) => {
  // 투명 여백을 제외한 실제 시계 그림(가로 약 7%, 세로 약 10%)의
  // 네 모서리가 모두 벽지 안에 남도록 검사한다.
  const leftBounds = getWallBounds(x - 3.5);
  const rightBounds = getWallBounds(x + 3.5);
  return {
    min: Math.max(leftBounds.top, rightBounds.top) + 5,
    max: Math.min(leftBounds.bottom, rightBounds.bottom) - 5,
  };
};
const fitWallPositionToWall = (position: WallPosition): WallPosition => {
  const x = Math.max(8, Math.min(92, position.x));
  const range = getWallYRange(x);
  return { x, y: Math.max(range.min, Math.min(range.max, position.y)) };
};
const percentToGrid = (left: number, top: number) => {
  const diagonalX = (left - ISO_CENTER_X) / ISO_TILE_X;
  const diagonalY = (top - ISO_TOP_Y) / ISO_TILE_Y;
  return { x: Math.round((diagonalX + diagonalY) / 2 - 0.5), y: Math.round((diagonalY - diagonalX) / 2 - 0.5) };
};
// Center of the visible floor diamond, independent of asymmetric placement bounds.
const CHARACTER_ROOM_POSITION = { left: 50, top: 64 };
// Furniture scales with the room; keep avatars proportional on desktop and mobile.
const CHARACTER_ROOM_SIZE = '18%';
// Align the avatar's foot baseline (832 / 896) with the floor center.
const CHARACTER_ROOM_ANCHOR = 'translate(-50%, -92.86%)';
const WALLPAPER_IMAGE_URLS: Partial<Record<RoomSurfaceKey, string>> = {
  wall_mint: '/images/student-profile/isometric-illustration-room-wall-mint-v2.png',
  wall_sky: '/images/student-profile/isometric-illustration-room-wall-sky-v2.png',
  wall_space: '/images/student-profile/isometric-illustration-room-wall-space-v2.png',
  wall_forest: '/images/student-profile/isometric-illustration-room-wall-forest-v2.png',
  wall_ocean: '/images/student-profile/isometric-illustration-room-wall-ocean-v2.png',
  wall_halloween: '/images/student-profile/isometric-illustration-room-wall-halloween-v2.png',
};
const FLOORING_IMAGE_URLS: Partial<Record<RoomSurfaceKey, string>> = {
  floor_walnut: '/images/student-profile/isometric-illustration-room-floor-walnut-v2.png',
  floor_carpet: '/images/student-profile/isometric-illustration-room-floor-carpet-v2.png',
  floor_space: '/images/student-profile/isometric-illustration-room-floor-space-v2.png',
  floor_forest: '/images/student-profile/isometric-illustration-room-floor-forest-v2.png',
  floor_ocean: '/images/student-profile/isometric-illustration-room-floor-ocean-v2.png',
  floor_halloween: '/images/student-profile/isometric-illustration-room-floor-halloween-v2.png',
};

const decor = FURNITURE_PRESETS.map((item) => ({ ...item, slots: [item.slot] as RoomSlot[] }));

const roomSlots: { key: RoomSlot; label: string }[] = [
  { key: 'wall', label: '벽' }, { key: 'desk', label: '책상 자리' },
  { key: 'floor', label: '바닥' }, { key: 'corner', label: '방 한쪽' },
  { key: 'bed', label: '침대 자리' },
];

const StudentProfile: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<DecorKey>('lamp');
  const [assignments, setAssignments] = useState<Partial<Record<RoomSlot, DecorKey>>>({});
  const [placements, setPlacements] = useState<FurniturePlacements>({});
  const [wallPosition, setWallPosition] = useState<WallPosition>({ x: 24, y: 34 });
  const [wallRotation, setWallRotation] = useState<FurnitureRotation>(0);
  const [draggingKey, setDraggingKey] = useState<DecorKey | null>(null);
  const roomRef = useRef<HTMLDivElement>(null);
  const dragOriginRef = useRef<{ key: DecorKey; pointer: { x: number; y: number }; placement: FurniturePlacement } | null>(null);
  const wallDragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const [visibility, setVisibility] = useState('우리 반만');
  const [status, setStatus] = useState('오늘의 기분: 신나요!');
  const [characterPreset, setCharacterPreset] = useState<CharacterPresetId>(DEFAULT_CHARACTER_PRESET);
  const [characterLoadout, setCharacterLoadout] = useState<CharacterLoadout>({});
  const [characterInventory, setCharacterInventory] = useState<string[]>([]);
  const [storeCharacterItems, setStoreCharacterItems] = useState<Record<string, { imageUrl: string; characterOutfitId?: string }>>({});
  const [ownedFurnitureStoreIds, setOwnedFurnitureStoreIds] = useState<string[]>([]);
  const [storeFurnitureItems, setStoreFurnitureItems] = useState<Record<string, FurnitureKey>>({});
  const [ownedSurfaceStoreIds, setOwnedSurfaceStoreIds] = useState<string[]>([]);
  const [storeSurfaceItems, setStoreSurfaceItems] = useState<Record<string, RoomSurfaceKey>>({});
  const [surfaces, setSurfaces] = useState<Partial<Record<RoomSurfaceSlot, RoomSurfaceKey>>>({});
  const name = currentUser?.name || '지호';
  const homeBasePath = currentUser?.schoolId && currentUser?.classId && currentUser?.uid
    ? `schools/${currentUser.schoolId}/classes/${currentUser.classId}/myHomes/${currentUser.uid}` : '';
  const classLabel = useMemo(() => {
    const classId = currentUser?.classId?.split('_');
    return classId?.length === 3 ? `${classId[1]}학년 ${classId[2]}반` : '우리 반';
  }, [currentUser?.classId]);

  useEffect(() => {
    if (!currentUser?.schoolId || !currentUser?.classId || !currentUser?.uid) return;
    const studentRef = doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students/${currentUser.uid}`);
    const storeRef = collection(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/storeItems`);
    const unsubscribeStudent = onSnapshot(studentRef, (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      setCharacterPreset(getCharacterPreset(data.characterPreset as string | undefined).id);
      setCharacterLoadout((data.characterLoadout as CharacterLoadout) || {});
      setCharacterInventory((data.characterInventory as string[]) || []);
      const ownedItems = Object.values((data.items as Record<string, { itemId?: string; itemCategory?: string }> | undefined) || {});
      setOwnedFurnitureStoreIds(ownedItems.filter((item) => item.itemCategory === 'furniture' && item.itemId).map((item) => item.itemId as string));
      setOwnedSurfaceStoreIds(ownedItems.filter((item) => item.itemCategory === 'roomSurface' && item.itemId).map((item) => item.itemId as string));
    });
    const unsubscribeStore = onSnapshot(storeRef, (snapshot) => {
      const next: Record<string, { imageUrl: string; characterOutfitId?: string }> = {};
      const nextFurniture: Record<string, FurnitureKey> = {};
      const nextSurfaces: Record<string, RoomSurfaceKey> = {};
      snapshot.docs.forEach((itemDoc) => {
        const data = itemDoc.data();
        if (data.itemCategory === 'cosmetic' && data.imageUrl) {
          next[itemDoc.id] = { imageUrl: data.imageUrl as string, characterOutfitId: data.characterOutfitId as string | undefined };
        }
        if (data.itemCategory === 'furniture' && FURNITURE_PRESETS.some((item) => item.key === data.furnitureKey)) nextFurniture[itemDoc.id] = data.furnitureKey as FurnitureKey;
        if (data.itemCategory === 'roomSurface' && ROOM_SURFACE_PRESETS.some((item) => item.key === data.roomSurfaceKey)) nextSurfaces[itemDoc.id] = data.roomSurfaceKey as RoomSurfaceKey;
      });
      setStoreCharacterItems(next);
      setStoreFurnitureItems(nextFurniture);
      setStoreSurfaceItems(nextSurfaces);
    });
    return () => {
      unsubscribeStudent();
      unsubscribeStore();
    };
  }, [currentUser?.classId, currentUser?.schoolId, currentUser?.uid]);

  const availableDecor = useMemo(() => {
    const keys = new Set(ownedFurnitureStoreIds.map((itemId) => storeFurnitureItems[itemId]).filter(Boolean));
    return decor.filter((item) => keys.has(item.key));
  }, [ownedFurnitureStoreIds, storeFurnitureItems]);
  const availableSurfaces = useMemo(() => {
    const keys = new Set(ownedSurfaceStoreIds.map((itemId) => storeSurfaceItems[itemId]).filter(Boolean));
    return ROOM_SURFACE_PRESETS.filter((item) => keys.has(item.key));
  }, [ownedSurfaceStoreIds, storeSurfaceItems]);

  useEffect(() => {
    if (availableDecor.length > 0 && !availableDecor.some((item) => item.key === selected)) {
      setSelected(availableDecor[0].key);
    }
  }, [availableDecor, selected]);

  useEffect(() => {
    if (!homeBasePath) return;
    const unsubscribeHome = onSnapshot(doc(db, homeBasePath), (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      if (data.visibility === '우리 반만' || data.visibility === '나만 보기') setVisibility(data.visibility);
      if (typeof data.status === 'string') setStatus(data.status);
      if (data.assignments && typeof data.assignments === 'object') setAssignments(data.assignments as Partial<Record<RoomSlot, DecorKey>>);
      if (data.placements && typeof data.placements === 'object') {
        setPlacements(fitPlacementsToGrid(data.placements as FurniturePlacements));
      } else if (data.assignments && typeof data.assignments === 'object') {
        const legacy = data.assignments as Partial<Record<RoomSlot, DecorKey>>;
        const migrated = Object.entries(legacy).reduce<FurniturePlacements>((result, [slot, key]) => {
          if (slot !== 'wall' && key) result[key] = { ...floorSlotDefaults[slot as Exclude<RoomSlot, 'wall'>], rotation: 0 };
          return result;
        }, {});
        setPlacements(migrated);
      }
      if (data.wallPosition && typeof data.wallPosition.x === 'number' && typeof data.wallPosition.y === 'number') {
        setWallPosition(fitWallPositionToWall(data.wallPosition as WallPosition));
      }
      if (data.wallRotation === 0 || data.wallRotation === 1) setWallRotation(data.wallRotation);
      if (data.surfaces && typeof data.surfaces === 'object') setSurfaces(data.surfaces as Partial<Record<RoomSurfaceSlot, RoomSurfaceKey>>);
    });
    return unsubscribeHome;
  }, [homeBasePath]);

  const saveHome = async (updates: Record<string, unknown>) => {
    if (!homeBasePath || !currentUser?.uid) return;
    try {
      await setDoc(doc(db, homeBasePath), { ownerUid: currentUser.uid, ...updates, updatedAt: serverTimestamp() }, { merge: true });
    } catch (error) { console.error(error); toast.error('마이홈 설정을 저장하지 못했습니다.'); }
  };

  const characterImage = useMemo(() => {
    const equippedBase = characterLoadout.base;
    if (equippedBase && characterInventory.includes(equippedBase) && storeCharacterItems[equippedBase]) {
      return getEquippedCharacterImageUrl(characterPreset, equippedBase, characterInventory, storeCharacterItems);
    }
    return getCharacterPreset(characterPreset).imageUrl;
  }, [characterInventory, characterLoadout.base, characterPreset, storeCharacterItems]);

  const equippedLayerImages = useMemo(() => {
    return CHARACTER_LAYER_RENDER_ORDER
      .filter((slot) => slot !== 'base')
      .map((slot) => characterLoadout[slot])
      .filter((itemId): itemId is string => Boolean(itemId && characterInventory.includes(itemId) && storeCharacterItems[itemId]))
      .map((itemId) => storeCharacterItems[itemId].imageUrl);
  }, [characterInventory, characterLoadout, storeCharacterItems]);

  const canPlaceFurniture = (key: DecorKey, candidate: FurniturePlacement, current = placements) => {
    const item = availableDecor.find((entry) => entry.key === key);
    if (!item || item.slot === 'wall') return false;
    const footprint = getFootprint(item, candidate.rotation);
    if (candidate.x < GRID_MIN_X || candidate.y < GRID_MIN_Y || candidate.x + footprint.width > GRID_COLUMNS || candidate.y + footprint.depth > GRID_ROWS) return false;
    return !Object.entries(current).some(([otherKey, otherPlacement]) => {
      if (otherKey === key || !otherPlacement) return false;
      const other = availableDecor.find((entry) => entry.key === otherKey);
      if (!other || other.slot === 'wall') return false;
      const otherFootprint = getFootprint(other, otherPlacement.rotation);
      return candidate.x < otherPlacement.x + otherFootprint.width && candidate.x + footprint.width > otherPlacement.x
        && candidate.y < otherPlacement.y + otherFootprint.depth && candidate.y + footprint.depth > otherPlacement.y;
    });
  };

  const selectFurniture = (key: DecorKey) => {
    setSelected(key);
    const item = availableDecor.find((entry) => entry.key === key);
    if (!item) return;
    if (item.slot === 'wall') {
      const next = { ...assignments, wall: key };
      setAssignments(next); void saveHome({ assignments: next, wallPosition, wallRotation }); return;
    }
    if (placements[key]) return;
    const preferred = { ...floorSlotDefaults[item.slot], rotation: 0 as FurnitureRotation };
    let target: FurniturePlacement | undefined = canPlaceFurniture(key, preferred) ? preferred : undefined;
    for (let y = GRID_MIN_Y; !target && y < GRID_ROWS; y += 1) for (let x = GRID_MIN_X; !target && x < GRID_COLUMNS; x += 1) {
      const candidate = { x, y, rotation: 0 as FurnitureRotation };
      if (canPlaceFurniture(key, candidate)) target = candidate;
    }
    if (!target) { toast.info('놓을 수 있는 빈 공간이 없어요.'); return; }
    const next = { ...placements, [key]: target };
    setPlacements(next); void saveHome({ placements: next });
  };

  const moveWallFurniture = (clientX: number, clientY: number, save = false) => {
    const room = roomRef.current;
    if (!room) return;
    const rect = room.getBoundingClientRect();
    const offset = wallDragOffsetRef.current || { x: 0, y: 0 };
    // 장식의 선택 영역 절반까지 고려해 양쪽 몰딩 밖으로 빠지지 않게 한다.
    const x = Math.max(8, Math.min(92, ((clientX - rect.left) / rect.width) * 100 - offset.x));
    const rawY = ((clientY - rect.top) / rect.height) * 100 - offset.y;
    const next = fitWallPositionToWall({ x, y: rawY });
    setWallPosition(next);
    if (save) void saveHome({ wallPosition: next });
  };

  const rotateWallFurniture = () => {
    const nextRotation = (wallRotation === 0 ? 1 : 0) as FurnitureRotation;
    const nextX = 100 - wallPosition.x;
    const range = getWallYRange(nextX);
    const nextPosition = { x: nextX, y: Math.max(range.min, Math.min(range.max, wallPosition.y)) };
    setWallRotation(nextRotation); setWallPosition(nextPosition);
    void saveHome({ wallRotation: nextRotation, wallPosition: nextPosition });
  };

  const removeWallFurniture = () => {
    const next = { ...assignments }; delete next.wall;
    setAssignments(next); void saveHome({ assignments: next });
  };

  const moveFurniture = (key: DecorKey, clientX: number, clientY: number, save = false) => {
    const room = roomRef.current;
    if (!room) return;
    const rect = room.getBoundingClientRect();
    const point = percentToGrid(((clientX - rect.left) / rect.width) * 100, ((clientY - rect.top) / rect.height) * 100);
    const current = placements[key];
    if (!current) return;
    const origin = dragOriginRef.current;
    const candidate = origin?.key === key
      ? { ...current, x: origin.placement.x + point.x - origin.pointer.x, y: origin.placement.y + point.y - origin.pointer.y }
      : { ...current, x: point.x, y: point.y };
    if (!canPlaceFurniture(key, candidate)) return;
    const next = { ...placements, [key]: candidate };
    setPlacements(next);
    if (save) void saveHome({ placements: next });
  };

  const rotateFurniture = (key: DecorKey) => {
    const current = placements[key];
    if (!current) return;
    const candidate = { ...current, rotation: (current.rotation === 0 ? 1 : 0) as FurnitureRotation };
    if (!canPlaceFurniture(key, candidate)) { toast.info('회전할 공간이 부족해요.'); return; }
    const next = { ...placements, [key]: candidate };
    setPlacements(next); void saveHome({ placements: next });
  };

  const removeFurniture = (key: DecorKey) => {
    const next = { ...placements }; delete next[key];
    setPlacements(next); void saveHome({ placements: next });
  };
  const applyFurnitureTheme = (theme: FurnitureTheme) => {
    const themed = availableDecor.filter((item) => item.theme === theme);
    const nextAssignments = themed.filter((item) => item.slot === 'wall').reduce<Partial<Record<RoomSlot, DecorKey>>>((result, item) => ({ ...result, wall: item.key }), {});
    const nextPlacements = themed.filter((item) => item.slot !== 'wall').reduce<FurniturePlacements>((result, item) => ({ ...result, [item.key]: { ...floorSlotDefaults[item.slot as Exclude<RoomSlot, 'wall'>], rotation: 0 } }), {});
    setAssignments(nextAssignments); setPlacements(nextPlacements);
    void saveHome({ assignments: nextAssignments, placements: nextPlacements });
  };
  const applySurface = (slot: RoomSurfaceSlot, key?: RoomSurfaceKey) => setSurfaces((current) => {
    const next = { ...current };
    if (key) next[slot] = key; else delete next[slot];
    void saveHome({ surfaces: next });
    return next;
  });

  return (
    <main className={styles.page}>
      <header className={styles.titleRow}>
        <div>
          <span className={styles.eyebrow}>나만의 작은 공간</span>
          <h1>{name}의 마루</h1>
        </div>
        <div className={styles.headerActions}>
          <label className={styles.visibility}>
            <span>공개 범위</span>
            <select value={visibility} onChange={(event) => { setVisibility(event.target.value); void saveHome({ visibility: event.target.value }); }}>
              <option>우리 반만</option>
              <option>나만 보기</option>
            </select>
          </label>
          <button className={styles.editButton} onClick={() => setEditing((value) => { if (value) void saveHome({ assignments, placements }); return !value; })}>
            {editing ? '꾸미기 완료' : '내 방 꾸미기'}
          </button>
          <button className={styles.editButton} onClick={() => navigate('/class/student/CharacterWardrobe')}>
            캐릭터 꾸미기
          </button>
        </div>
      </header>

      <div className={styles.workspace}>
        <section className={styles.roomSection} aria-label="나의 미니룸">
          {editing && (
            <aside className={styles.decorTray} aria-label="가구 선택">
              <strong>가구함</strong>
              {(['space', 'forest', 'ocean', 'halloween'] as FurnitureTheme[]).map((theme) => {
                const ownedCount = availableDecor.filter((item) => item.theme === theme).length;
                const themeItemCount = decor.filter((item) => item.theme === theme).length;
                return ownedCount === themeItemCount ? <button key={theme} className={styles.themeApply} onClick={() => applyFurnitureTheme(theme)}><span>✨</span>{FURNITURE_THEME_LABELS[theme]} 세트 적용</button> : null;
              })}
              {availableDecor.map((item) => (
                <button
                  key={item.key}
                  className={selected === item.key ? styles.decorSelected : ''}
                  onClick={() => selectFurniture(item.key)}
                  aria-pressed={selected === item.key}
                >
                  <span aria-hidden="true">{item.icon}</span>{item.label}{item.theme && <small>{FURNITURE_THEME_LABELS[item.theme]}</small>}
                </button>
              ))}
              {availableDecor.length === 0 && <p className={styles.decorHelp}>보유한 가구가 없어요. 학급 상점에서 가구를 구매해 보세요.</p>}
              <strong className={styles.surfaceTitle}>벽지</strong>
              <button className={!surfaces.wallpaper ? styles.decorSelected : ''} onClick={() => applySurface('wallpaper')}><span>🏠</span>기본 벽지</button>
              {availableSurfaces.filter((item) => item.slot === 'wallpaper').map((item) => <button key={item.key} className={surfaces.wallpaper === item.key ? styles.decorSelected : ''} onClick={() => applySurface('wallpaper', item.key)}><span>{item.icon}</span>{item.label}</button>)}
              <strong className={styles.surfaceTitle}>바닥</strong>
              <button className={!surfaces.flooring ? styles.decorSelected : ''} onClick={() => applySurface('flooring')}><span>🪵</span>기본 바닥</button>
              {availableSurfaces.filter((item) => item.slot === 'flooring').map((item) => <button key={item.key} className={surfaces.flooring === item.key ? styles.decorSelected : ''} onClick={() => applySurface('flooring', item.key)}><span>{item.icon}</span>{item.label}</button>)}
              <p className={styles.decorHelp}>가구를 누르면 방에 놓여요. 방 안에서 끌어서 옮겨 보세요.</p>
            </aside>
          )}
          <div ref={roomRef} className={`${styles.room} ${editing ? styles.roomEditing : ''}`}>
            <img
              src={(surfaces.wallpaper && WALLPAPER_IMAGE_URLS[surfaces.wallpaper]) || '/images/student-profile/isometric-illustration-room-empty-v2.png'}
              alt="아이소메트릭 나무 바닥과 창문이 있는 일러스트 미니룸"
            />
            {surfaces.flooring && availableSurfaces.some((item) => item.key === surfaces.flooring) && FLOORING_IMAGE_URLS[surfaces.flooring] && (
              <img className={styles.surfaceIllustrationLayer} src={FLOORING_IMAGE_URLS[surfaces.flooring]} alt="" aria-hidden="true" />
            )}
            {assignments.wall && availableDecor.some((item) => item.key === assignments.wall) && (() => {
              const item = availableDecor.find((entry) => entry.key === assignments.wall)!;
              // 벽 원근감은 살리되 원형 시계가 과하게 찌그러지지 않도록 약하게 적용한다.
              const wallSkew = wallPosition.x < 50 ? -9 : 9;
              return <div
                className={`${styles.wallFurniture} ${selected === item.key && editing ? styles.wallFurnitureSelected : ''} ${draggingKey === item.key ? styles.isoFurnitureDragging : ''}`}
                style={{ left: `${wallPosition.x}%`, top: `${wallPosition.y}%` }}
                onPointerDown={(event) => {
                  if (!editing) return;
                  event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId);
                  const room = roomRef.current;
                  if (room) {
                    const rect = room.getBoundingClientRect();
                    wallDragOffsetRef.current = {
                      x: ((event.clientX - rect.left) / rect.width) * 100 - wallPosition.x,
                      y: ((event.clientY - rect.top) / rect.height) * 100 - wallPosition.y,
                    };
                  }
                  setSelected(item.key); setDraggingKey(item.key);
                }}
                onPointerMove={(event) => { if (editing && draggingKey === item.key) moveWallFurniture(event.clientX, event.clientY); }}
                onPointerUp={(event) => {
                  if (draggingKey !== item.key) return;
                  moveWallFurniture(event.clientX, event.clientY, true);
                  wallDragOffsetRef.current = null; setDraggingKey(null);
                }}
                aria-label={`${item.label}, 벽 장식`}
              ><img
                src={item.imageUrl}
                alt={`벽의 ${item.label}`}
                draggable={false}
                style={{ transform: `${wallRotation === 1 ? 'scaleX(-1) ' : ''}skewY(${wallSkew}deg) scaleX(.96)` }}
              /></div>;
            })()}
            {Object.entries(placements).map(([itemKey, placement]) => {
              if (!placement) return null;
              const item = availableDecor.find((entry) => entry.key === itemKey);
              if (!item || item.slot === 'wall') return null;
              const footprint = getFootprint(item, placement.rotation);
              // 배치 좌표의 북쪽 꼭짓점과 이미지의 북쪽 꼭짓점을 함께 고정한다.
              // 중심 반전이 아니라 이미지 상단 꼭짓점(약 28%)을 축으로 반전한다.
              const baseFootprint = getFootprint(item, 0);
              const anchorFootprint = item.floorAnchor ? footprint : baseFootprint;
              const position = gridToPercent(
                placement.x + anchorFootprint.width / 2,
                placement.y + anchorFootprint.depth / 2,
              );
              const width = item.slot === 'bed' ? 28 : item.slot === 'desk' ? 24 : item.slot === 'floor' ? 26 : footprint.width >= 3 ? 27 : footprint.width === 2 ? 21 : 13;
              return <div
                key={itemKey}
                className={`${styles.isoFurniture} ${selected === itemKey && editing ? styles.isoFurnitureSelected : ''} ${draggingKey === itemKey ? styles.isoFurnitureDragging : ''}`}
                style={{ left: `${position.left}%`, top: `${position.top}%`, width: `${item.renderWidth ?? width}%`, transform: item.floorAnchor ? `translate(-50%, -${item.floorAnchor}%)` : undefined, zIndex: item.slot === 'floor' ? 2 : 3 + placement.x + placement.y }}
                onPointerDown={(event) => {
                  if (!editing) return;
                  event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId);
                  const room = roomRef.current;
                  if (room) {
                    const rect = room.getBoundingClientRect();
                    dragOriginRef.current = {
                      key: item.key,
                      pointer: percentToGrid(((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100),
                      placement: { ...placement },
                    };
                  }
                  setSelected(item.key); setDraggingKey(item.key);
                }}
                onPointerMove={(event) => { if (editing && draggingKey === item.key) moveFurniture(item.key, event.clientX, event.clientY); }}
                onPointerUp={(event) => {
                  if (draggingKey !== item.key) return;
                  moveFurniture(item.key, event.clientX, event.clientY, true); dragOriginRef.current = null; setDraggingKey(null);
                }}
                aria-label={`${item.label}, ${placement.x + 1}열 ${placement.y + 1}행`}
              >
                <img
                  src={placement.rotation === 1 && item.rotationImageUrl ? item.rotationImageUrl : item.imageUrl}
                  alt={item.label}
                  draggable={false}
                  style={placement.rotation === 1 && !item.rotationImageUrl ? { transform: 'scaleX(-1)', transformOrigin: item.floorAnchor ? '50% 50%' : '28% 0%' } : undefined}
                />
                {editing && selected === item.key && <span className={styles.footprintLabel}>{footprint.width}×{footprint.depth}</span>}
              </div>;
            })}
            {!editing && <>
              <IllustratedCharacter
                className={styles.character}
                src={characterImage}
                alt={`${name}의 캐릭터`}
                idle={equippedLayerImages.length === 0}
                style={{ position: 'absolute', width: CHARACTER_ROOM_SIZE, height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', left: `${CHARACTER_ROOM_POSITION.left}%`, top: `${CHARACTER_ROOM_POSITION.top}%`, bottom: 'auto', transform: CHARACTER_ROOM_ANCHOR, zIndex: 20 }}
              />
              {equippedLayerImages.map((imageUrl) => (
                <img
                  key={imageUrl}
                  className={styles.character}
                  src={imageUrl}
                  alt=""
                  style={{ width: CHARACTER_ROOM_SIZE, height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', left: `${CHARACTER_ROOM_POSITION.left}%`, top: `${CHARACTER_ROOM_POSITION.top}%`, bottom: 'auto', transform: CHARACTER_ROOM_ANCHOR, pointerEvents: 'none', zIndex: 21 }}
                />
              ))}
              <MyHomePet editing={false} />
            </>}
            {editing && <div className={styles.isoGrid} aria-hidden="true">{Array.from({ length: (GRID_COLUMNS - GRID_MIN_X) * (GRID_ROWS - GRID_MIN_Y) }, (_, index) => {
              const columnCount = GRID_COLUMNS - GRID_MIN_X;
              const x = index % columnCount + GRID_MIN_X; const y = Math.floor(index / columnCount) + GRID_MIN_Y; const position = gridCellToPercent(x, y);
              return <i key={index} style={{ left: `${position.left}%`, top: `${position.top}%` }} />;
            })}</div>}
            {editing && availableDecor.length > 0 && <div className={styles.editHint}>가구를 끌어서 옮기고, 선택한 가구를 회전할 수 있어요</div>}
            {editing && (placements[selected] || assignments.wall === selected) && <div className={styles.furnitureControls}>
              <strong>{availableDecor.find((item) => item.key === selected)?.label}</strong>
              <button onClick={() => assignments.wall === selected ? rotateWallFurniture() : rotateFurniture(selected)}>↻ 회전</button>
              <button onClick={() => assignments.wall === selected ? removeWallFurniture() : removeFurniture(selected)}>치우기</button>
            </div>}
          </div>
        </section>

        <aside className={styles.sidebar}>
          <section className={styles.profileSummary}>
            <div className={styles.avatar} style={{ position: 'relative' }}>
              <IllustratedCharacter
                src={characterImage}
                alt={`${name}의 프로필 캐릭터`}
                style={{ width: 55, height: 66, objectFit: 'contain', objectPosition: 'center 8px' }}
              />
              {equippedLayerImages.map((imageUrl) => (
                <img
                  key={imageUrl}
                  src={imageUrl}
                  alt=""
                  style={{ position: 'absolute', width: 55, height: 66, objectFit: 'contain', objectPosition: 'center 8px' }}
                />
              ))}
            </div>
            <div>
              <p className={styles.classLabel}>{classLabel}</p>
              <h2>{name}의 마루</h2>
            </div>
            <div className={styles.badgeRow}>
              <span>칭찬 배지</span><strong>12개</strong>
            </div>
          </section>

          <section className={styles.statusCard}>
            <div className={styles.sectionTitle}><h2>내 상태 메시지</h2><span>{status.length}/40</span></div>
            <textarea
              className={styles.statusInput}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              onBlur={() => void saveHome({ status })}
              placeholder="오늘의 기분이나 한마디를 적어보세요"
              maxLength={40}
              rows={3}
              aria-label="내 상태 메시지"
            />
            <p className={styles.statusHint}>입력한 메시지는 마이홈을 방문한 친구에게 보여요.</p>
          </section>
        </aside>
      </div>
    </main>
  );
};

export default StudentProfile;
